// Weergave-maskering voor demo-accounts (16-09-2026).
//
// De server anonimiseert klanten, verkopers en teams (utils/demo.js), maar laat
// EAN's, klantnummers en factuurnummers staan: dat zijn de sleutels waarmee de
// schermen orders, facturen, meetgegevens en bill shocks aan elkaar koppelen. Ze
// server-side maskeren kan niet — "eerste 6 + laatste 3 cijfers" is niet uniek
// (Power2You: 1.549 van de 2.301 EAN's zouden botsen) en dan lopen de dossiers
// door elkaar. Daarom maskeren we bij het TONEN:
//   - EAN 541448860013543413 → 541448•••••••••413, in elke tabel, kop, tooltip
//     en badge — één waarnemer op het document i.p.v. een wijziging op elk van
//     de ~70 plekken waar een EAN gerenderd wordt (en zonder dat een nieuwe
//     tabel het kan vergeten);
//   - voor een prospect ook het bronlabel in klantnummers en factuurnummers:
//     P2Y/2024/11/63 → ZON/2024/11/63, VOP2Y/202402/00004 → VOZON/202402/00004,
//     en elke resterende tekst "Power2You" → de naam van het potentiële label.
// De data zelf (React-state, props, URL-parameters, klembord via de kopieerknop)
// blijft onaangeroerd; alleen tekstknopen en title-attributen in het DOM worden
// herschreven. Exports lopen niet via het DOM en gaan apart door `maskeerCel`
// (exportRowsToCsv), waar klantnummers en factuurnummers helemaal wegvallen.

import { prospectActief, prospectAfkorting, prospectLabel } from './prospect';

const EAN = /\b(\d{6})\d{9}(\d{3})\b/g;
const BRON_TOKEN = /\b(VO|VK|SB)?P2Y(?=\/)/g;
const BRON_NAAM = /power\s*2\s*you/gi;

let actief = false;

export const demoWeergaveActief = () => actief;

export function maskeerEan(s) {
  return String(s == null ? '' : s).replace(EAN, '$1•••••••••$2');
}

// Eén tekst maskeren: EAN's altijd, het bronlabel enkel voor een prospect.
export function maskeerTekst(s) {
  if (typeof s !== 'string' || !s) return s;
  let uit = s.replace(EAN, '$1•••••••••$2');
  if (prospectActief()) {
    const afk = prospectAfkorting();
    uit = uit.replace(BRON_TOKEN, (m, pre) => `${pre || ''}${afk}`).replace(BRON_NAAM, prospectLabel());
  }
  return uit;
}

// Voor exports: een cel maskeren zoals het scherm dat doet.
export const maskeerCel = (v) => (typeof v === 'string' ? maskeerTekst(v) : v);

// Kolommen die in een export van een demo-account helemaal wegvallen:
// klantnummers en factuurnummers (vraag 16-09-2026). EAN-kolommen blijven,
// gemaskeerd.
const IDENT_KOP = /klant(en)?nummer|klantref|customer\s*ref|^nummer$|factuurn(umme)?r|invoice\s*(number|nr)|^ref$|referentie$/i;
export const isIdentificatieKolom = (kop) => IDENT_KOP.test(String(kop || '').trim());

// ── DOM-waarnemer ────────────────────────────────────────────────────────────
const OVERSLAAN = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT']);

function maskeerTekstknoop(node) {
  const oud = node.nodeValue;
  if (!oud) return;
  const nieuw = maskeerTekst(oud);
  if (nieuw !== oud) node.nodeValue = nieuw;   // gelijk → niets doen, anders een lus van mutaties
}
function maskeerTitel(el) {
  if (!el || !el.getAttribute) return;
  const oud = el.getAttribute('title');
  if (!oud) return;
  const nieuw = maskeerTekst(oud);
  if (nieuw !== oud) el.setAttribute('title', nieuw);
}
function maskeerBoom(root) {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) { maskeerTekstknoop(root); return; }
  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    if (OVERSLAAN.has(root.tagName)) return;
    maskeerTitel(root);
    for (const el of root.querySelectorAll('[title]')) maskeerTitel(el);
  }
  const loper = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.parentNode && OVERSLAAN.has(n.parentNode.tagName) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const knopen = [];
  for (let n = loper.nextNode(); n; n = loper.nextNode()) knopen.push(n);
  for (const n of knopen) maskeerTekstknoop(n);
}

let waarnemer = null;

// Start de maskering op het hele document; geeft een stopfunctie terug.
// Idempotent: een tweede start vervangt de eerste niet.
export function startDemoWeergave(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || actief) return stopDemoWeergave;
  actief = true;
  maskeerBoom(doc.body);
  waarnemer = new MutationObserver((mutaties) => {
    for (const m of mutaties) {
      if (m.type === 'characterData') maskeerTekstknoop(m.target);
      else if (m.type === 'attributes') maskeerTitel(m.target);
      else for (const n of m.addedNodes) maskeerBoom(n);
    }
  });
  waarnemer.observe(doc.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['title'] });
  return stopDemoWeergave;
}

export function stopDemoWeergave() {
  if (waarnemer) { waarnemer.disconnect(); waarnemer = null; }
  actief = false;
}
